import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSort, faSortUp, faSortDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

/**
 * Renderiza automáticamente el valor de una celda
 */
const autoRenderCell = (row, column) => {
  const value = row[column.key];
  
  // Si hay render personalizado, usarlo
  if (column.render) {
    return column.render(row);
  }
  
  // Detectar tipo del campo
  const fieldType = column.type ?? column.formConfig?.type;
  
  // 🎉 Si es tipo 'image', renderizar imagen automáticamente
  if (fieldType === 'image' && value) {
    return (
      <img 
        src={`${value}?v=${new Date().getTime()}`} 
        alt="" 
        className="preview-image-grid" 
      />
    );
  }
  
  // Renderizado automático según tipo
  if (value === null || value === undefined) {
    return '';
  }

  if (fieldType === 'select' && Array.isArray(column.options)) {
    const matchedOption = column.options.find(
      (option) => String(option?.value) === String(value),
    );
    if (matchedOption && matchedOption.label !== undefined && matchedOption.label !== null) {
      return matchedOption.label;
    }
  }
  
  // Si es un objeto (ej: category.description), intentar acceder a 'description' o 'name'
  if (typeof value === 'object' && value !== null) {
    return value.description || value.name || JSON.stringify(value);
  }
  
  // Si es booleano
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }
  
  // Si es número con type 'number', formatear
  const decimals = column.decimals ?? column.formConfig?.decimals;
  if (typeof value === 'number' && fieldType === 'number') {
    return value.toFixed(decimals || 2);
  }

  if (shouldFormatAsDate(value, fieldType)) {
    return formatDateValue(value, findRelatedTimeValue(row, column.key));
  }
  
  // Si es string y tiene maxLengthInGrid, truncar
  const maxLength = column.maxLengthInGrid ?? column.formConfig?.maxLengthInGrid;
  if (typeof value === 'string' && maxLength && value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  
  // Por defecto, convertir a string
  return String(value);
};

const ISO_DATE_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/;
const ISO_DATE_TIME_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/;

const shouldFormatAsDate = (value, fieldType) => {
  if (value instanceof Date) {
    return true;
  }
  if (fieldType === "date" || fieldType === "datetime" || fieldType === "dateTime") {
    return true;
  }
  return typeof value === "string" && ISO_DATE_VALUE_PATTERN.test(value);
};

const findRelatedTimeValue = (row, key) => {
  if (!row || !key) {
    return null;
  }

  const normalizedKey = String(key);
  const lowerKey = normalizedKey.toLowerCase();
  const candidates = [];

  if (lowerKey.endsWith('date')) {
    const prefix = normalizedKey.slice(0, -4);
    candidates.push(`${prefix}time`, `${prefix}Time`);
  }

  return candidates
    .map((candidate) => row[candidate])
    .find((candidateValue) => candidateValue !== null && candidateValue !== undefined && candidateValue !== '') ?? null;
};

const parseTimeOfDay = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return null;
    }
    return {
      hours: Math.floor(seconds / 3600) % 24,
      minutes: Math.floor((seconds % 3600) / 60),
      seconds: Math.floor(seconds % 60),
    };
  }

  const match = String(value).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  return {
    hours: Number(match[1]),
    minutes: Number(match[2]),
    seconds: Number(match[3] || 0),
  };
};

const formatDateValue = (value, relatedTimeValue = null) => {
  const dateTimeMatch = typeof value === "string"
    ? value.match(ISO_DATE_TIME_VALUE_PATTERN)
    : null;
  const relatedTime = parseTimeOfDay(relatedTimeValue);
  const date = value instanceof Date
    ? new Date(value.getTime())
    : (() => {
        const match = String(value).match(ISO_DATE_VALUE_PATTERN);
        if (!match) {
          return null;
        }
        const [, year, month, day] = match;
        const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
        if (dateTimeMatch) {
          parsedDate.setHours(
            Number(dateTimeMatch[4] || 0),
            Number(dateTimeMatch[5] || 0),
            Number(dateTimeMatch[6] || 0),
            0,
          );
        }
        return parsedDate;
      })();

  if (!date || Number.isNaN(date.getTime())) {
    return String(value);
  }

  if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && relatedTime) {
    date.setHours(relatedTime.hours, relatedTime.minutes, relatedTime.seconds, 0);
  }

  const hasMeaningfulTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;

  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(hasMeaningfulTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  }).format(date);
};

const toStableKeyString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => toStableKeyString(item)).join(',')}]`;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${key}:${toStableKeyString(value[key])}`).join(',')}}`;
  }
  return String(value);
};

const resolveRowKey = (row, rowIndex) => {
  const joinedRowKey = row?.__joinedRowKey;
  if (joinedRowKey !== null && joinedRowKey !== undefined && String(joinedRowKey)) {
    return String(joinedRowKey);
  }

  const idValue = row?.id;
  if (idValue !== null && idValue !== undefined) {
    const stableId = toStableKeyString(idValue);
    if (stableId) {
      return stableId;
    }
  }

  if (row && typeof row === 'object') {
    const fallbackBusinessKey = row.iSalesId ?? row.salesId ?? row.recId;
    if (fallbackBusinessKey !== null && fallbackBusinessKey !== undefined) {
      return String(fallbackBusinessKey);
    }
  }

  return `row-${rowIndex}`;
};

const MIN_RESIZABLE_COLUMN_WIDTH = 40;
const MAX_RESIZABLE_COLUMN_WIDTH = 2000;

const normalizeGridColumnWidth = (value) => {
  const width = Math.round(typeof value === "string" ? parseFloat(value) : Number(value));
  return Number.isFinite(width) ? width : null;
};

const clampGridColumnWidth = (width, column) => {
  return Math.max(MIN_RESIZABLE_COLUMN_WIDTH, Math.min(MAX_RESIZABLE_COLUMN_WIDTH, Math.round(width)));
};

/**
 * DataGrid reutilizable con sorting, paginación y acciones
 * 
 * @param {Array} columns - Definición de columnas
 *   [{
 *     key: 'name',
 *     label: 'Name',
 *     sortable: true,
 *     render: (row) => row.name,  // OPCIONAL: si no se especifica, usa autoRenderCell
 *     className: 'text-center',
 *     formConfig: { type: 'number', decimals: 2 }  // Para formateo automático
 *   }]
 * 
 * Renderizado automático:
 * - Campos simples: muestra el valor directamente
 * - Objetos: intenta acceder a .description o .name
 * - Números con formConfig.type='number': formatea con decimales
 * - Booleanos: muestra ✓ o ✗
 * - null/undefined: muestra ''
 * 
 * @param {Array} data - Datos a mostrar
 * @param {Object} sortConfig - { key: 'name', direction: 'ascending' }
 * @param {Function} onSort - Callback para ordenar
 * @param {Function} onEdit - Callback para editar (row)
 * @param {Function} onDelete - Callback para eliminar (row)
 * @param {Boolean} hasEnabledToggle - Si tiene toggle de enabled
 * @param {Function} onEnabledChange - Callback para cambiar enabled (id, value)
 * @param {Object} pagination - { currentPage, totalPages, totalElements, numberOfElements, pageSize }
 * @param {Function} onPageChange - Callback para cambiar página
 * @param {Function} onPageSizeChange - Callback para cambiar tamaño de página (opcional)
 */
const DataGrid = ({
  columns = [],
  data = [],
  sortConfig = { key: null, direction: 'ascending' },
  onSort,
  onEdit,
  onDelete,
  renderRowActions = null,
  onRowDoubleClick,
  hasEnabledToggle = false,
  onEnabledChange,
  pagination,
  onPageChange,
  onPageSizeChange,
  showTopPagination = false,
  rowStyleResolver = null,
  topPaginationActions = null,
  onColumnResize = null,
}) => {
  const { t } = useTranslation();
  const topScrollRef = useRef(null);
  const topScrollInnerRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);
  const syncingRef = useRef(false);
  const holdScrollTimeoutRef = useRef(null);
  const holdScrollIntervalRef = useRef(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [floatingHeaderVisible, setFloatingHeaderVisible] = useState(false);
  const [floatingHeaderTop, setFloatingHeaderTop] = useState(0);
  const [floatingHeaderLeft, setFloatingHeaderLeft] = useState(0);
  const [floatingHeaderWidth, setFloatingHeaderWidth] = useState(0);
  const [floatingHeaderScrollLeft, setFloatingHeaderScrollLeft] = useState(0);
  const [floatingHeaderColWidths, setFloatingHeaderColWidths] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const resizeCleanupRef = useRef(null);

  const visibleColumns = columns.filter((col) => {
    if (!(col.showInGrid ?? true)) {
      return false;
    }
    if (typeof col.showInGridWhenAnyRowMatches === 'function') {
      return data.some((row) => col.showInGridWhenAnyRowMatches(row));
    }
    return true;
  });
  const hasRowActions = Boolean(onEdit || onDelete || renderRowActions);

  useEffect(() => {
    const nextWidths = {};
    columns.forEach((column) => {
      const width = normalizeGridColumnWidth(column?.gridWidth);
      if (column?.key && width) {
        nextWidths[column.key] = clampGridColumnWidth(width, column);
      }
    });
    setColumnWidths(nextWidths);
  }, [columns]);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        return <FontAwesomeIcon icon={faSortUp} />;
      } else {
        return <FontAwesomeIcon icon={faSortDown} />;
      }
    } else {
      return <FontAwesomeIcon icon={faSort} />;
    }
  };

  useEffect(() => {
    const topEl = topScrollRef.current;
    const topInnerEl = topScrollInnerRef.current;
    const bottomEl = bottomScrollRef.current;
    if (!topEl || !topInnerEl || !bottomEl) {
      return undefined;
    }

    const syncWidth = () => {
      const scrollWidth = bottomEl.scrollWidth;
      const clientWidth = bottomEl.clientWidth;
      topInnerEl.style.width = `${scrollWidth}px`;
      const hasOverflow = scrollWidth > clientWidth + 1;
      setHasHorizontalOverflow(hasOverflow);
      if (hasOverflow) {
        topEl.classList.add('is-visible');
      } else {
        topEl.classList.remove('is-visible');
      }
      const maxLeft = Math.max(0, scrollWidth - clientWidth);
      setCanScrollLeft(bottomEl.scrollLeft > 0);
      setCanScrollRight(bottomEl.scrollLeft < maxLeft - 1);
      setFloatingHeaderWidth(clientWidth);
      setFloatingHeaderScrollLeft(bottomEl.scrollLeft);
    };

    const onTopScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      bottomEl.scrollLeft = topEl.scrollLeft;
      syncingRef.current = false;
    };

    const onBottomScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      topEl.scrollLeft = bottomEl.scrollLeft;
      syncingRef.current = false;
      const maxLeft = Math.max(0, bottomEl.scrollWidth - bottomEl.clientWidth);
      setCanScrollLeft(bottomEl.scrollLeft > 0);
      setCanScrollRight(bottomEl.scrollLeft < maxLeft - 1);
      setFloatingHeaderScrollLeft(bottomEl.scrollLeft);
    };

    syncWidth();
    topEl.addEventListener('scroll', onTopScroll, { passive: true });
    bottomEl.addEventListener('scroll', onBottomScroll, { passive: true });
    window.addEventListener('resize', syncWidth);

    return () => {
      topEl.removeEventListener('scroll', onTopScroll);
      bottomEl.removeEventListener('scroll', onBottomScroll);
      window.removeEventListener('resize', syncWidth);
    };
  }, [data.length, visibleColumns.length, hasEnabledToggle, hasRowActions, columnWidths]);

  const nudgeHorizontalScroll = (direction) => {
    const topEl = topScrollRef.current;
    if (!topEl) return;
    const delta = Math.max(120, Math.floor(topEl.clientWidth * 0.25));
    topEl.scrollBy({ left: direction * delta, behavior: 'smooth' });
  };

  const stepHorizontalScroll = (direction) => {
    const topEl = topScrollRef.current;
    if (!topEl) return;
    const step = Math.max(10, Math.floor(topEl.clientWidth * 0.02));
    topEl.scrollLeft += direction * step;
  };

  const clearHoldScroll = useCallback(() => {
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    if (holdScrollIntervalRef.current) {
      clearInterval(holdScrollIntervalRef.current);
      holdScrollIntervalRef.current = null;
    }
    window.removeEventListener('mouseup', clearHoldScroll);
    window.removeEventListener('touchend', clearHoldScroll);
    window.removeEventListener('touchcancel', clearHoldScroll);
  }, []);

  const startHoldScroll = (direction) => {
    clearHoldScroll();
    // Comportamiento similar al scrollbar nativo: breve espera y avance continuo.
    holdScrollTimeoutRef.current = setTimeout(() => {
      holdScrollIntervalRef.current = setInterval(() => {
        stepHorizontalScroll(direction);
      }, 16);
    }, 220);
    window.addEventListener('mouseup', clearHoldScroll);
    window.addEventListener('touchend', clearHoldScroll, { passive: true });
    window.addEventListener('touchcancel', clearHoldScroll, { passive: true });
  };

  useEffect(() => () => clearHoldScroll(), [clearHoldScroll]);

  useEffect(() => {
    const computeStickyTop = () => {
      const appHeaderEl = document.querySelector('.header');
      const pageHeaderEl = document.querySelector('.page-header');
      const appHeaderBottom = appHeaderEl
        ? Math.max(0, appHeaderEl.getBoundingClientRect().bottom)
        : 0;
      const pageHeaderBottom = pageHeaderEl
        ? Math.max(0, pageHeaderEl.getBoundingClientRect().bottom)
        : 0;

      // Use the largest visible fixed/sticky header bottom as viewport anchor.
      return Math.max(appHeaderBottom, pageHeaderBottom);
    };

    const updateFloatingHeader = () => {
      const bottomEl = bottomScrollRef.current;
      const tableEl = tableRef.current;
      if (!bottomEl || !tableEl) {
        setFloatingHeaderVisible(false);
        return;
      }

      const headerCells = Array.from(tableEl.querySelectorAll('thead th'));
      if (headerCells.length === 0) {
        setFloatingHeaderVisible(false);
        return;
      }

      const stickyTop = computeStickyTop();
      const tableRect = tableEl.getBoundingClientRect();
      const scrollerRect = bottomEl.getBoundingClientRect();
      const headerHeight = headerCells[0].getBoundingClientRect().height || 0;

      const shouldShow =
        tableRect.top < stickyTop &&
        tableRect.bottom > stickyTop + headerHeight;

      setFloatingHeaderVisible(shouldShow);
      setFloatingHeaderTop(stickyTop);
      setFloatingHeaderLeft(scrollerRect.left);
      setFloatingHeaderWidth(scrollerRect.width);
      setFloatingHeaderScrollLeft(bottomEl.scrollLeft);
      setFloatingHeaderColWidths(headerCells.map((cell) => cell.getBoundingClientRect().width));
    };

    updateFloatingHeader();
    window.addEventListener('scroll', updateFloatingHeader, { passive: true });
    window.addEventListener('resize', updateFloatingHeader);

    return () => {
      window.removeEventListener('scroll', updateFloatingHeader);
      window.removeEventListener('resize', updateFloatingHeader);
    };
  }, [data.length, visibleColumns.length, hasEnabledToggle, hasRowActions, columnWidths]);

  const stopColumnResize = useCallback(() => {
    if (resizeCleanupRef.current) {
      resizeCleanupRef.current();
      resizeCleanupRef.current = null;
    }
  }, []);

  useEffect(() => () => stopColumnResize(), [stopColumnResize]);

  const startColumnResize = useCallback((event, column) => {
    if (!column?.key || typeof onColumnResize !== "function") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    stopColumnResize();

    const startX = event.clientX;
    const headerCell = event.currentTarget.closest("th");
    const currentWidth = columnWidths[column.key]
      || normalizeGridColumnWidth(column.gridWidth)
      || Math.round(headerCell?.getBoundingClientRect().width || 0)
      || 120;
    const startWidth = clampGridColumnWidth(currentWidth, column);
    let latestWidth = startWidth;

    document.body.classList.add("idax-grid-column-resizing");

    const handleMove = (moveEvent) => {
      const nextWidth = clampGridColumnWidth(startWidth + moveEvent.clientX - startX, column);
      latestWidth = nextWidth;
      setColumnWidths((current) => ({
        ...current,
        [column.key]: nextWidth,
      }));
    };

    const handleEnd = () => {
      stopColumnResize();
      if (latestWidth !== startWidth) {
        onColumnResize(column.key, latestWidth);
      }
    };

    const cleanup = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      document.body.classList.remove("idax-grid-column-resizing");
    };

    resizeCleanupRef.current = cleanup;
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
  }, [columnWidths, onColumnResize, stopColumnResize]);

  const renderHeaderContent = (column, translatedLabel) => (
    <>
      <span className="idax-grid-header-label">
        {translatedLabel} {column.sortable && onSort && getSortIcon(column.key)}
      </span>
      {typeof onColumnResize === "function" && (
        <span
          className="idax-grid-column-resizer"
          onMouseDown={(event) => startColumnResize(event, column)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          aria-hidden="true"
        />
      )}
    </>
  );

  const resolveGridColumnWidth = (column) => {
    const preferredWidth = columnWidths[column.key];
    const width = preferredWidth || normalizeGridColumnWidth(column?.gridWidth);
    return width ? `${width}px` : undefined;
  };

  return (
    <>
      {floatingHeaderVisible && (
        <div
          className="idax-floating-grid-header"
          style={{
            top: `${floatingHeaderTop}px`,
            left: `${floatingHeaderLeft}px`,
            width: `${floatingHeaderWidth}px`,
          }}
        >
          <div
            className="idax-floating-grid-header-scroll"
            style={{ transform: `translateX(${-floatingHeaderScrollLeft}px)` }}
          >
            <table className="table table-bordered table-striped mb-none idax-data-grid-table idax-data-grid-table-floating">
              <colgroup>
                {visibleColumns.map((column, idx) => (
                  <col
                    key={`floating-col-${column.key}`}
                    style={{
                      width: floatingHeaderColWidths[idx]
                        ? `${floatingHeaderColWidths[idx]}px`
                        : resolveGridColumnWidth(column),
                    }}
                  />
                ))}
                {hasEnabledToggle && (
                  <col
                    style={{
                      width: floatingHeaderColWidths[visibleColumns.length]
                        ? `${floatingHeaderColWidths[visibleColumns.length]}px`
                        : undefined,
                    }}
                  />
                )}
                {hasRowActions && (
                  <col
                    style={{
                      width: floatingHeaderColWidths[visibleColumns.length + (hasEnabledToggle ? 1 : 0)]
                        ? `${floatingHeaderColWidths[visibleColumns.length + (hasEnabledToggle ? 1 : 0)]}px`
                        : undefined,
                    }}
                  />
                )}
              </colgroup>
              <thead>
                <tr>
                  {visibleColumns.map((column, idx) => {
                    const translatedLabel = typeof column.label === 'string' && column.label.includes('.')
                      ? t(column.label)
                      : column.label;

                    return (
                      <th
                        key={`floating-${column.key}`}
                        onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                        style={{
                          whiteSpace: 'nowrap',
                          cursor: column.sortable ? 'pointer' : 'default',
                          width: floatingHeaderColWidths[idx] ? `${floatingHeaderColWidths[idx]}px` : undefined,
                          minWidth: floatingHeaderColWidths[idx] ? `${floatingHeaderColWidths[idx]}px` : undefined,
                          maxWidth: floatingHeaderColWidths[idx] ? `${floatingHeaderColWidths[idx]}px` : undefined,
                          ...resolveGridColumnStyle(column, columnWidths[column.key]),
                        }}
                        className={`${column.headerClassName || ''} ${typeof onColumnResize === "function" ? "idax-grid-resizable-header" : ""}`.trim()}
                      >
                        {renderHeaderContent(column, translatedLabel)}
                      </th>
                    );
                  })}
                  {hasEnabledToggle && (
                    <th
                      className="actions-column"
                      style={{
                        width: floatingHeaderColWidths[visibleColumns.length]
                          ? `${floatingHeaderColWidths[visibleColumns.length]}px`
                          : undefined,
                      }}
                    >
                      {t('common.enabled')}
                    </th>
                  )}
                  {(onEdit || onDelete) && (
                    <th
                      className="actions-column"
                      style={{
                        width: floatingHeaderColWidths[visibleColumns.length + (hasEnabledToggle ? 1 : 0)]
                          ? `${floatingHeaderColWidths[visibleColumns.length + (hasEnabledToggle ? 1 : 0)]}px`
                          : undefined,
                      }}
                    >
                      {t('common.actions')}
                    </th>
                  )}
                </tr>
              </thead>
            </table>
          </div>
        </div>
      )}

      {pagination && showTopPagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          numberOfElements={pagination.numberOfElements}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          summaryActions={topPaginationActions}
        />
      )}
      
      <div className={`table-top-scroll-wrap ${hasHorizontalOverflow ? 'is-visible' : ''}`}>
        <button
          type="button"
          className="table-top-scroll-arrow"
          onClick={() => nudgeHorizontalScroll(-1)}
          onMouseDown={() => startHoldScroll(-1)}
          onMouseUp={clearHoldScroll}
          onMouseLeave={clearHoldScroll}
          onTouchStart={() => startHoldScroll(-1)}
          onTouchEnd={clearHoldScroll}
          disabled={!canScrollLeft}
          aria-label={t('common.pagination.previous')}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="table-top-scroll" ref={topScrollRef} aria-hidden="true">
          <div className="table-top-scroll-inner" ref={topScrollInnerRef} />
        </div>
        <button
          type="button"
          className="table-top-scroll-arrow"
          onClick={() => nudgeHorizontalScroll(1)}
          onMouseDown={() => startHoldScroll(1)}
          onMouseUp={clearHoldScroll}
          onMouseLeave={clearHoldScroll}
          onTouchStart={() => startHoldScroll(1)}
          onTouchEnd={clearHoldScroll}
          disabled={!canScrollRight}
          aria-label={t('common.pagination.next')}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      <div className="table-responsive" ref={bottomScrollRef}>
        <table ref={tableRef} className="table table-bordered table-striped mb-none idax-data-grid-table">
          <colgroup>
            {visibleColumns.map((column) => (
              <col
                key={`col-${column.key}`}
                style={{
                  width: resolveGridColumnWidth(column),
                }}
              />
            ))}
            {hasEnabledToggle && <col />}
            {hasRowActions && <col />}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((column) => {
                  // 🎉 Traducir label si es una clave de traducción
                  const translatedLabel = typeof column.label === 'string' && column.label.includes('.') 
                    ? t(column.label) 
                    : column.label;
                  
                  return (
                    <th
                      key={column.key}
                      onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                      style={{ 
                        whiteSpace: 'nowrap',
                        cursor: column.sortable ? 'pointer' : 'default',
                        ...resolveGridColumnStyle(column, columnWidths[column.key]),
                      }}
                      className={`${column.headerClassName || ''} ${typeof onColumnResize === "function" ? "idax-grid-resizable-header" : ""}`.trim()}
                    >
                      {renderHeaderContent(column, translatedLabel)}
                    </th>
                  );
                })}
              
              {hasEnabledToggle && (
                <th className="actions-column">{t('common.enabled')}</th>
              )}
              
              {(onEdit || onDelete) && (
                <th className="actions-column">{t('common.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + (hasEnabledToggle ? 1 : 0) + (onEdit || onDelete ? 1 : 0)} style={{ textAlign: 'center', padding: '20px' }}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowKey = resolveRowKey(row, rowIndex);
                const resolvedRowStyle =
                  typeof rowStyleResolver === "function"
                    ? rowStyleResolver(row)
                    : null;
                const rowDecoration =
                  resolvedRowStyle &&
                  typeof resolvedRowStyle === "object" &&
                  Object.prototype.hasOwnProperty.call(resolvedRowStyle, "style")
                    ? resolvedRowStyle
                    : null;
                const rowStyle = rowDecoration ? rowDecoration.style : resolvedRowStyle;
                const rowTitle = rowDecoration ? rowDecoration.title || rowDecoration.hoverText || "" : "";
                const mergedRowStyle = {
                  ...(rowStyle || {}),
                  ...(onRowDoubleClick ? { cursor: "pointer" } : {}),
                };
                const mergedCellStyle = rowStyle ? { ...rowStyle } : undefined;

                return (
                <tr
                  key={rowKey}
                  onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                  style={Object.keys(mergedRowStyle).length > 0 ? mergedRowStyle : undefined}
                  title={rowTitle || undefined}
                >
                  {visibleColumns.map((column) => (
                      <td
                        key={`${rowKey}-${column.key}`}
                        className={column.className || ''}
                        style={{ ...(mergedCellStyle || {}), ...resolveGridColumnStyle(column, columnWidths[column.key]) }}
                      >
                        {autoRenderCell(row, column)}
                      </td>
                    ))}
                  
                  {hasEnabledToggle && (
                    <td className='actions-column' style={mergedCellStyle}>
                      <input
                        type="checkbox"
                        checked={row.enabled || false}
                        onChange={(e) => onEnabledChange && onEnabledChange(row.id, e.target.checked)}
                      />
                    </td>
                  )}
                  
                  {hasRowActions && (
                    <td className='actions-column' style={mergedCellStyle}>
                      {renderRowActions ? renderRowActions(row) : null}
                      {onEdit && (
                        <FontAwesomeIcon
                          icon={faEdit}
                          onClick={() => onEdit(row)}
                          className="action-icon action-edit"
                          title={t('common.edit')}
                        />
                      )}
                      {onDelete && (
                        <FontAwesomeIcon
                          icon={faTrash}
                          onClick={() => onDelete(row)}
                          className="action-icon action-delete"
                          title={t('common.delete')}
                        />
                      )}
                    </td>
                  )}
                </tr>
              )})
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          numberOfElements={pagination.numberOfElements}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </>
  );
};

export default DataGrid;

  const resolveGridColumnStyle = (column, preferredWidth = null) => {
    const style = {};
    const hasPreferredWidth = preferredWidth !== undefined && preferredWidth !== null && preferredWidth !== "";
    const width = preferredWidth || column?.gridWidth;
    const minWidth = column?.gridMinWidth;
    const maxWidth = column?.gridMaxWidth;
    const hasWidthConstraint =
      (width !== undefined && width !== null && width !== "") ||
      (minWidth !== undefined && minWidth !== null && minWidth !== "") ||
      (maxWidth !== undefined && maxWidth !== null && maxWidth !== "");

    if (width !== undefined && width !== null && width !== "") {
      style.width = typeof width === "number" ? `${width}px` : String(width);
      style.minWidth = typeof width === "number" ? `${width}px` : String(width);
      if (hasPreferredWidth) {
        style.maxWidth = typeof width === "number" ? `${width}px` : String(width);
      }
    }
    if (!width && minWidth !== undefined && minWidth !== null && minWidth !== "") {
      style.minWidth = typeof minWidth === "number" ? `${minWidth}px` : String(minWidth);
    }
    if (!hasPreferredWidth && maxWidth !== undefined && maxWidth !== null && maxWidth !== "") {
      style.maxWidth = typeof maxWidth === "number" ? `${maxWidth}px` : String(maxWidth);
    }
    if (hasWidthConstraint) {
      style.overflow = "hidden";
      style.textOverflow = "ellipsis";
    }
    return style;
  };

